/* ==========================================================================
   LscNexus — Transportation & Mobility Solutions
   A division of Bethel Innovations, LLC.
   Source: Website-Updates.docx (used close to verbatim).
   ========================================================================== */

export const lscnexus = {
  name: 'LscNexus',
  parentLine: 'A division of Bethel Innovations, LLC',
  title: 'Transportation & Mobility Solutions',
  summary:
    'Advanced transportation planning, logistics, artificial intelligence, GIS and data analytics that help communities and transportation agencies make safer, smarter and more informed decisions.',

  intro: [
    'LscNexus, a division of Bethel Innovations, LLC, provides advanced transportation planning, logistics, artificial intelligence, GIS, and data analytics solutions that help communities and transportation agencies make safer, smarter, and more informed decisions.',
    'Bethel Innovations provides transportation and traffic engineering expertise supporting traffic operations, transportation planning, roadway safety, Intelligent Transportation Systems (ITS), and multimodal mobility. Our capabilities include traffic analysis, signal-timing optimization, traffic simulation, safety assessments, roadway design support, and operational performance evaluation.',
    'We help transportation agencies and communities improve mobility, safety, efficiency and system performance through data-driven engineering analysis and practical transportation solutions.',
  ],

  approach: [
    'Our transportation practice combines expertise in multimodal transportation safety, crash and risk analysis, GIS and geospatial analytics, statistical modeling, traffic operations, active transportation, and roadway-safety evaluation.',
    'We apply data-driven methods and emerging technologies to understand transportation challenges, identify safety risks, evaluate infrastructure needs, and develop practical strategies that improve mobility and community safety.',
    'LscNexus supports transportation agencies, communities and stakeholders throughout the planning and decision-making lifecycle — from data collection and analysis through visualization, evaluation, technical reporting and implementation planning.',
  ],

  /* Engagement lifecycle.
     Not invented — this is the sequence stated verbatim in the source copy:
     "…throughout the planning and decision-making lifecycle — from data
     collection and analysis through visualization, evaluation, technical
     reporting, and implementation planning."
     Surfacing it as a sequence rather than leaving it inside a paragraph. */
  lifecycle: [
    {
      title: 'Data collection & analysis',
      body: 'Crash, near-miss and community-generated safety data gathered and analysed using GIS, statistical methods and AI.',
    },
    {
      title: 'Visualization',
      body: 'Complex transportation data turned into clear maps, dashboards and visualizations.',
    },
    {
      title: 'Evaluation',
      body: 'Before-and-after assessment of countermeasures and operational performance against project data.',
    },
    {
      title: 'Technical reporting',
      body: 'Findings documented for agency leaders, stakeholders and the communities affected.',
    },
    {
      title: 'Implementation planning',
      body: 'Actionable recommendations, grant development and practical strategies agencies can fund and deliver.',
    },
  ],

  capabilities: [
    {
      title: 'Transportation Safety & Crash Analysis',
      body: 'Analyze crash, near-miss, and community-generated transportation safety data using GIS, statistical methods, and artificial intelligence to identify trends, risks, and opportunities for intervention.',
    },
    {
      title: 'GIS & Geospatial Analytics',
      body: 'Develop spatial analysis methods to identify priority intersections, corridors, geographic areas, and contributing safety factors.',
    },
    {
      title: 'Multimodal & Active Transportation',
      body: 'Evaluate pedestrian, bicycle, and micromobility infrastructure needs using community-centered approaches to improve accessibility, connectivity, and safety.',
    },
    {
      title: 'Roadway Safety Countermeasure Evaluation',
      body: 'Conduct before-and-after evaluations of roadway safety improvements using transportation project data to assess effectiveness and support evidence-based investment decisions.',
    },
    {
      title: 'Traffic Operations & Engineering Analysis',
      body: 'Perform traffic operations, intersection, capacity, and simulation analyses using Synchro, SimTraffic, VISSIM, TransModeler, and Highway Capacity Manual (HCM) methodologies.',
    },
    {
      title: 'Data Analytics & Artificial Intelligence',
      body: 'Apply advanced analytics and AI techniques to transportation data to identify patterns, support predictive insights, and improve transportation planning and safety decision-making.',
    },
    {
      title: 'Visualization & Technical Reporting',
      body: 'Transform complex transportation data into clear maps, dashboards, visualizations, technical reports, and actionable recommendations for agency leaders, stakeholders, and communities.',
    },
    {
      title: 'Grant & Implementation Support',
      body: 'Support transportation initiatives through technical documentation, project evaluation, implementation planning, and grant development.',
    },
  ],

  csap: {
    title: 'Comprehensive Safety Action Planning',
    body: [
      'LscNexus brings these capabilities together to support Comprehensive Safety Action Plans (CSAPs) and other transportation safety initiatives.',
      'Our multidisciplinary approach integrates crash and risk analysis, GIS, traffic engineering, multimodal safety, community input, data analytics and performance evaluation to help communities identify high-priority safety challenges and develop practical, measurable strategies for reducing serious injuries and fatalities.',
    ],
  },

  integration: {
    title: 'Integrated Technology & Transportation Expertise',
    body: [
      'Through LscNexus, Bethel Innovations combines transportation engineering, AI, GIS, data analytics and digital technology to deliver practical, evidence-based solutions for modern transportation challenges.',
      'Our multidisciplinary capabilities enable clients to move from data to insight, insight to strategy, and strategy to measurable transportation safety outcomes — supporting safer, more connected, accessible and resilient communities.',
    ],
  },

  /* Named tooling — real, from the source document. Useful proof for agencies. */
  tools: ['Synchro', 'SimTraffic', 'VISSIM', 'TransModeler', 'Highway Capacity Manual (HCM)'],
};
